import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBullhorn } from "@fortawesome/free-solid-svg-icons";
import CHANGELOG, { versionActual } from "../../changelog";
import { esDesarrollo, versionDev } from "../../AppVersion";
import IconVectorLeft from "../../assets/icons/vectorLeft.svg";

// Bitácora de cambios (`src/changelog.js`) visible ÚNICAMENTE en el ambiente de
// desarrollo. Se entra desde el botón que está junto a la versión, en el sidebar
// (`BotonNovedades`), y se muestra como una vista completa: todas las versiones,
// de la más reciente a la más antigua, con sus cambios.

const STORAGE_KEY = "nimbus_changelog_version_vista";

// localStorage puede fallar (modo privado / almacenamiento bloqueado): nada de
// esto debe tumbar la app, así que se ignora el error y solo se pierde la marca.
const versionVista = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    return versionActual; // sin acceso: no marcamos novedades para no insistir
  }
};

export const hayNovedadesSinVer = () =>
  Boolean(esDesarrollo && versionActual) && versionVista() !== versionActual;

export const marcarNovedadesVistas = () => {
  try {
    window.localStorage.setItem(STORAGE_KEY, versionActual);
  } catch (error) {
    /* sin persistencia: el aviso se volverá a mostrar en la siguiente carga */
  }
};

const TIPOS = {
  nuevo: { etiqueta: "Nuevo", clases: "bg-green-100 text-green-700" },
  mejora: { etiqueta: "Mejora", clases: "bg-nimbus/10 text-nimbusDark" },
  correccion: { etiqueta: "Corrección", clases: "bg-amber-100 text-amber-700" },
  eliminado: { etiqueta: "Eliminado", clases: "bg-red-100 text-red-600" },
};

const formatearFecha = (fecha) => {
  const [anio, mes, dia] = (fecha || "").split("-");
  if (!anio || !mes || !dia) return fecha ?? "";
  const meses = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
  ];
  return `${Number(dia)} de ${meses[Number(mes) - 1]} de ${anio}`;
};

/**
 * Botón que va junto a la versión en el sidebar. Solo aparece en desarrollo y
 * marca un punto cuando la versión actual todavía no se ha revisado.
 */
export const BotonNovedades = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hayNovedades, setHayNovedades] = useState(false);

  // Se revisa en cada navegación: así el punto desaparece también cuando se
  // entra a la vista por URL directa, sin pasar por este botón.
  useEffect(() => {
    setHayNovedades(hayNovedadesSinVer());
  }, [location.pathname]);

  if (!esDesarrollo || CHANGELOG.length === 0) return null;

  const abrir = () => {
    marcarNovedadesVistas();
    setHayNovedades(false);
    navigate("/novedades");
  };

  return (
    <button
      type="button"
      onClick={abrir}
      title="Novedades de esta versión"
      aria-label="Novedades de esta versión"
      className="relative flex items-center justify-center gap-1 px-1.5 py-1 rounded-md text-[10px] font-medium text-nimbusDark hover:bg-gray-100 hover:text-lightBlue transition-colors focus:outline-none"
    >
      <FontAwesomeIcon icon={faBullhorn} className="w-3 h-3" />
      <span>Novedades</span>
      {hayNovedades && (
        <span className="absolute top-0 right-0 flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lightBlue opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-lightBlue" />
        </span>
      )}
    </button>
  );
};

const Novedades = () => {
  const navigate = useNavigate();
  // Solo la versión más reciente arranca abierta: con muchas versiones, verlas
  // todas desplegadas amontona la pantalla.
  const [versionesAbiertas, setVersionesAbiertas] = useState([versionActual]);

  useEffect(() => {
    marcarNovedadesVistas();
  }, []);

  const alternarVersion = (version) =>
    setVersionesAbiertas((prev) =>
      prev.includes(version) ? prev.filter((v) => v !== version) : [...prev, version]
    );

  return (
    <div className="bg-backgroundColor pb-4 min-h-[calc(100vh-4.25rem)] flex flex-col">
      <div className="flex flex-col md:flex-row justify-between md:p-4 items-center">
        <div className="flex md:p-4 my-4 md:my-0">
          <div
            className="opacity-80 cursor-pointer flex text-base md:text-xl text-nimbusDark mr-1"
            onClick={() => navigate("/home")}
          >
            Inicio /
          </div>
          <p className="text-base md:text-xl text-nimbusDark font-semibold">Novedades</p>
        </div>
      </div>

      <div className="mt-2">
        <div
          className="flex items-center px-2 w-20 cursor-pointer"
          onClick={() => navigate(-1)}
        >
          <img className="px-2 mt-0.5 w-6" src={IconVectorLeft} alt="" />
          <p className="font-bold text-lg text-[#1A3D82]">Atrás</p>
        </div>
      </div>

      <div className="px-4 md:px-10 mt-6 flex flex-col gap-8 w-full">
        {!esDesarrollo || CHANGELOG.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 text-sm text-gray-600">
            La bitácora de cambios solo está disponible en el ambiente de desarrollo.
          </div>
        ) : (
          <>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h1 className="text-xl font-semibold text-nimbusDark">
                Novedades de la plataforma
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Cambios publicados en cada versión. Estás en la versión{" "}
                <strong className="text-nimbusDark">{versionDev}</strong>.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Esta bitácora solo se muestra en el ambiente de <strong>desarrollo</strong>.
              </p>
            </div>

            {/* Una sección por versión; sus cambios se acomodan en cuadrícula para
                aprovechar el ancho de la pantalla (una sola columna en móvil). */}
            {CHANGELOG.map(({ version, fecha, cambios = [] }, index) => {
              const abierta = versionesAbiertas.includes(version);
              return (
                <section key={version} className="flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={() => alternarVersion(version)}
                    aria-expanded={abierta}
                    className="w-full flex flex-wrap items-center gap-2 border-b border-gray-200 pb-2 text-left hover:border-nimbusDark transition-colors focus:outline-none"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`w-4 h-4 text-gray-400 transition-transform ${abierta ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                    <span className="text-lg font-bold text-nimbusDark">{version}</span>
                    {index === 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-lightBlue/15 text-[10px] font-bold uppercase tracking-wide text-nimbusDark">
                        Actual
                      </span>
                    )}
                    <span className="text-xs text-gray-500">{formatearFecha(fecha)}</span>
                    <span className="flex-1 text-xs text-gray-500 text-right">
                      {cambios.length} cambio{cambios.length === 1 ? "" : "s"}
                    </span>
                  </button>

                  {abierta && (
                    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                      {cambios.map(({ tipo, modulo, descripcion }, i) => {
                        const cfg = TIPOS[tipo] ?? TIPOS.mejora;
                        return (
                          <article
                            key={i}
                            className="h-full flex flex-col gap-2 bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${cfg.clases}`}
                              >
                                {cfg.etiqueta}
                              </span>
                              {modulo && (
                                <span className="text-xs font-semibold text-nimbusDark truncate">
                                  {modulo}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 leading-snug">{descripcion}</p>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default Novedades;
